import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import RawCaptureTimelinePreview from '../components/Debug/RawCaptureTimelinePreview';
import ResponsiveLayout from '../components/Layout/ResponsiveLayout';
import { useRole } from '../contexts/RoleContext';
import CaptureFirstExtractionPanel from '../features/rawCareCaptureExtraction/CaptureFirstExtractionPanel';
import { buildCareExtractionContext } from '../utils/careExtractionContext';
import { isDebugFlagEnabled } from '../utils/debugTools';
import { extractCareTimelineRecords } from '../utils/rawCaptureExtractor';

const LONG_HISTORY_SAMPLE = `May 3 around 7:15 AM difficult transition before school kept asking for blue shoes then got upset when they were wet crying yelling but calmed after mom sat with him for a few minutes medication given as usual at 7:30 bus pickup was fine afterward

April 11 had a really good afternoon after OT wanted to stay outside for almost an hour watched airplanes and birds no aggression appetite still a little low since increasing guanfacine but mood overall much calmer this week

March 22 2:45 PM school called said he pushed chair during math transition level 3 about 5 minutes quiet room helped afterward came home tired and skipped most of dinner but asked for snack later before bed

February 8 slept poorly last night awake around 1:30 AM wandering hallway asking for tablet this morning seemed very emotional refused breakfast but eventually ate crackers after medication at 8:00 AM school reported better afternoon overall

January 17 around 4 PM went to park with Aaron seemed excited and ran around laughing for long time one brief moment covering ears near basketball court but recovered quickly after moving near trees no aggression rest of evening calm`;

const VOICE_DAY_SAMPLE = 'Today he woke up around 10 AM, slept pretty restfully, had breakfast, took his usual morning Depakote, and we are going out for a walk. It is around 90 degrees, overcast, nice, and drizzling a little.';

const SOURCE_TYPES = ['text', 'voice', 'import', 'unknown'];

const isRawCaptureSandboxEnabled = () => isDebugFlagEnabled('debugRawCaptureExtractor', {
  allowWithoutFlag: true,
});

// Sandbox entry point for docs/experiments/raw-care-capture-extraction.md.
const RawCaptureExtractorSandboxPage = () => {
  const isEnabled = isRawCaptureSandboxEnabled();
  const { childrenWithAccess = [] } = useRole();
  const [text, setText] = useState(VOICE_DAY_SAMPLE);
  const [sourceType, setSourceType] = useState('voice');
  const [selectedChildId, setSelectedChildId] = useState('');
  const [result, setResult] = useState(() => extractCareTimelineRecords({
    rawText: VOICE_DAY_SAMPLE,
    sourceType: 'voice',
  }));
  const selectedChild = childrenWithAccess.find((child) => child.id === selectedChildId) || null;
  const extractionContext = buildCareExtractionContext(selectedChild);

  useEffect(() => {
    if (!selectedChildId && childrenWithAccess.length === 1) {
      setSelectedChildId(childrenWithAccess[0].id);
    }
  }, [childrenWithAccess, selectedChildId]);

  const handleExtract = () => {
    setResult(extractCareTimelineRecords({
      rawText: text,
      sourceType,
    }));
  };

  const loadSample = (sampleText, nextSourceType) => {
    setText(sampleText);
    setSourceType(nextSourceType);
    setResult(extractCareTimelineRecords({
      rawText: sampleText,
      sourceType: nextSourceType,
    }));
  };

  if (!isEnabled) {
    return (
      <ResponsiveLayout pageTitle="Raw Capture Extractor" showBottomNav={false}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">
            Raw capture extractor sandbox is only available on localhost or the test deploy.
          </Alert>
        </Container>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout pageTitle="Raw Capture Extractor" showBottomNav={false}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={1.5}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Raw Capture Extractor
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Paste caregiving text and inspect the intermediate care records returned by the experimental extractor.
              Saving is limited to the capture-first sandbox path and does not change Quick Log or import behavior.
            </Typography>
          </Stack>
        </Paper>

        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <Button variant="outlined" onClick={() => loadSample(VOICE_DAY_SAMPLE, 'voice')}>
                  Load voice sample
                </Button>
                <Button variant="outlined" onClick={() => loadSample(LONG_HISTORY_SAMPLE, 'import')}>
                  Load history sample
                </Button>
                <Button variant="text" component={RouterLink} to="/admin">
                  Back to Admin
                </Button>
              </Stack>

              <FormControl fullWidth>
                <InputLabel id="raw-capture-child-context-label">Profile context</InputLabel>
                <Select
                  labelId="raw-capture-child-context-label"
                  label="Profile context"
                  value={selectedChildId}
                  onChange={(event) => setSelectedChildId(event.target.value)}
                >
                  <MenuItem value="">No profile context</MenuItem>
                  {childrenWithAccess.map((child) => (
                    <MenuItem key={child.id} value={child.id}>
                      {child.name || 'Unnamed profile'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="raw-capture-source-type-label">Source type</InputLabel>
                <Select
                  labelId="raw-capture-source-type-label"
                  label="Source type"
                  value={sourceType}
                  onChange={(event) => setSourceType(event.target.value)}
                >
                  {SOURCE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Raw caregiving note"
                multiline
                minRows={8}
                fullWidth
                value={text}
                onChange={(event) => setText(event.target.value)}
              />

              <Box>
                <Button variant="contained" onClick={handleExtract}>
                  Extract records
                </Button>
              </Box>
            </Stack>
          </Paper>

          {result ? (
            <>
              <CaptureFirstExtractionPanel rawText={text} sourceType={sourceType} child={selectedChild} />

              {extractionContext ? (
                <Paper sx={{ p: 3 }}>
                  <Stack spacing={1}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Extraction context preview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This profile context is sent only to the experimental backend AI comparison. It is not saved with the note.
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: 'grey.100',
                        overflow: 'auto',
                        fontSize: '0.82rem',
                      }}
                    >
                      {JSON.stringify(extractionContext, null, 2)}
                    </Box>
                  </Stack>
                </Paper>
              ) : null}

              <Paper sx={{ p: 3 }}>
                <RawCaptureTimelinePreview
                  title="Local timeline preview"
                  description="Shows the local parser output as timeline-style cards. This is preview-only and does not save records."
                  records={result.records}
                />
              </Paper>

            </>
          ) : null}
        </Stack>

        <Divider sx={{ my: 4 }} />

        <Button component={RouterLink} to="/admin/natural-capture-debug" variant="outlined">
          Natural Capture Debug
        </Button>
      </Container>
    </ResponsiveLayout>
  );
};

export default RawCaptureExtractorSandboxPage;
